import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { Invoice, InvoiceDetail } from "@/lib/types"

interface InvoiceFilters {
  status?: string
  invoice_type?: string
  company_id?: string
  deal_id?: string
  issue_date_from?: string
  issue_date_to?: string
  due_date_from?: string
  due_date_to?: string
  sort?: string
}

function buildParams(filters: InvoiceFilters): string {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function useInvoices(filters: InvoiceFilters = {}) {
  return useQuery({
    queryKey: ["invoices", filters],
    queryFn: () => {
      const qs = buildParams(filters)
      return api.get<{ data: Invoice[] }>(`/api/v1/invoices${qs ? `?${qs}` : ""}`)
    },
  })
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ["invoices", id],
    queryFn: () => api.get<{ data: InvoiceDetail }>(`/api/v1/invoices/${id}`),
    enabled: !!id,
  })
}

export function useCreateInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: InvoiceDetail }>("/api/v1/invoices", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export function useUpdateInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: InvoiceDetail }>(`/api/v1/invoices/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export function useDeleteInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/invoices/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export function useIssueInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: Invoice }>(`/api/v1/invoices/${id}/issue`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export function useSendInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: Invoice }>(`/api/v1/invoices/${id}/send_invoice`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export function useRecordPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { amount: number; payment_date: string; payment_method?: string; reference?: string; notes?: string } }) =>
      api.post<{ data: InvoiceDetail }>(`/api/v1/invoices/${id}/record_payment`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export function useCreateCorrectiveInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { correction_reason: string } }) =>
      api.post<{ data: InvoiceDetail }>(`/api/v1/invoices/${id}/corrective`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export function useCreateInvoiceFromDeal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dealId: string) =>
      api.post<{ data: InvoiceDetail }>("/api/v1/invoices/from_deal", { deal_id: dealId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export function useCreateInvoiceFromQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (quoteId: string) =>
      api.post<{ data: InvoiceDetail }>("/api/v1/invoices/from_quote", { quote_id: quoteId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}
